const MomentTz = require('moment-timezone')
const MomentRange = require('moment-range')
const moment = MomentRange.extendMoment(MomentTz)

/**
 * Generates the day with incidents data structure in the form:
 *
 * {
 * '2019-09-13': { status: 'outage', incidents: [ [Object] ] },
 * '2019-09-14': { status: 'normal', incidents: [] },
 * '2019-09-15': { status: 'normal', incidents: [] },
 * '2019-09-16': { status: 'degradation', incidents: [ [Object] ] },
 * '2019-09-17': { status: 'normal', incidents: [] },
 * '2019-09-18': { status: 'normal', incidents: [] },
 * '2019-09-19': { status: 'normal', incidents: [] },
 * '2019-09-20': { status: 'normal', incidents: [] },
 * '2019-09-21': { status: 'normal', incidents: [] },
 * '2019-09-22': { status: 'normal', incidents: [] },
 * '2019-09-23': { status: 'normal', incidents: [] },
 * '2019-09-24': { status: 'normal', incidents: [] },
 * '2019-09-25': { status: 'normal', incidents: [] },
 * '2019-09-26': { status: 'normal', incidents: [] },
 * '2019-09-27': { status: 'normal', incidents: [] },
 * '2019-09-28': { status: 'normal', incidents: [] },
 * '2019-09-29': { status: 'degradation', incidents: [ [Object] ] },
 * '2019-09-30': { status: 'normal', incidents: [] },
 * '2019-10-01': { status: 'normal', incidents: [] }
 * ...
 * }
 *
 * for the UI to consume and turn into a visual representation.
 */
const genDays = (service, incidents, incidentServices, inTimezone, asOfDate, daysBack, dateOffsetBugfixEnabled = false) => {
  // Define until we can use flatMap() or flat()
  const flatten = (arr, result = []) => {
    for (let i = 0, length = arr.length; i < length; i++) {
      const value = arr[i]
      if (Array.isArray(value)) {
        flatten(value, result)
      } else {
        result.push(value)
      }
    }
    return result
  }

  /**
   * Decides the status of the day given the statuses of all the incidents on that day
   */
  const decideDayStatusForService = incidentsForDay => {
    // Return immediately if there are no incidents on this day
    const incidentsExistForDay = incidentsForDay.length > 0
    if (!incidentsExistForDay) {
      return "normal"
    }

    const incidentIdsForDay = incidentsForDay.map(i => i.id)
    const incidentServicesForService = incidentServices.filter(i => i.attributes.serviceId.toString() === service.id)
    const incidentServicesForDay = incidentServicesForService.filter(i => incidentIdsForDay.includes(i.attributes.incidentId.toString()))

    if (incidentServicesForDay.length === 0) {
      return "normal"
    } else if (incidentServicesForDay.some(i => i.attributes.outage)) {
      return "outage"
    }
    return "degradation"
  }

  /**
   * Gathers all the incidents for this service
   */
  const activeIncidentsForService = () => {
    // Return immediately if there are no incidents for any service
    const incidentsExistForAnyService = incidents && incidents.length > 0

    if (!incidentsExistForAnyService) {
      return []
    }

    // Find the incidents that have affected services that match this service
    const incidentsForService = flatten(
      incidents.filter(incident => {
        const affectedServiceIds = incidentServices.filter(i => i.attributes.incidentId.toString() === incident.id).map(is => is.attributes.serviceId.toString())
        return affectedServiceIds.includes(service.id)
      }))

    return incidentsForService
  }

  /**
   * Returns an array of dates this incident appears on
   */
  const appearsOnDates = incident => {
    const incidentServicesForIncident = incidentServices.filter(i => i.attributes.incidentId.toString() === incident.id)
    const incidentService = incidentServicesForIncident.filter(i => i.attributes.serviceId.toString() === service.id)[0]

    const startedAt = incidentService && incidentService.attributes && incidentService.attributes.startedAt
    const startedAtUtc = moment.tz(startedAt, "UTC")
    const startedAtTz = startedAtUtc.tz(inTimezone).format('YYYY-MM-DD')

    const resolvedAt = incidentService && incidentService.attributes && incidentService.attributes.resolvedAt
    const resolvedAtUtc = resolvedAt !== null ? moment.tz(resolvedAt, "UTC") : moment.tz("UTC")
    const resolvedAtTz = resolvedAtUtc.tz(inTimezone).format('YYYY-MM-DD')

    // console.log("startedAtTz=", startedAtTz)
    // console.log("resolvedAtTz=", resolvedAtTz)

    const incidentCoversRange = moment.range(startedAtTz, resolvedAtTz)

    return Array.from(incidentCoversRange.by('days')).map(day => day.format('YYYY-MM-DD'))
  }

  /**
   * Groups the list of incidents passed in by their incident.id
   */
  const groupByIncidentId = ins => {
    return ins.reduce((acc, incident) => {
      acc[incident.id] = incident
      return acc
    }, {})
  }

  /**
   * Groups the list of incidents passed in by the day they appear on
   */
  const groupIncidentsByDay = ins => {
    // Group each incident by id for later retrieval by id
    const incidentsById = groupByIncidentId(ins)

    // Group each incident by incident_id and what dates it appears on
    const incidentsHasDates = ins.reduce((acc, incident) => {
      acc[incident.id] = appearsOnDates(incident)
      return acc
    }, {})

    // Reverse the grouping so it's grouped by date with a list
    // incidents that belong to that date
    const datesHaveIncidents = Object.keys(incidentsHasDates).reduce((acc, key) => {
      incidentsHasDates[key].forEach(date => {
        if (!acc[date]) acc[date] = []
        acc[date].push(incidentsById[key])
      })
      return acc
    }, {})

    return datesHaveIncidents
  }

  /**
   * Generate the days with incidents that occurred on that day
   */
  const generateDays = (incidentsByDay = {}) => {
    const asOfDateTz = (dateOffsetBugfixEnabled) ? moment.utc().add(1, 'd').tz(inTimezone) : asOfDate.tz(inTimezone)

    // Range is usually 90 days back starting from today
    const range = moment.rangeFromInterval('day', -(daysBack-1), asOfDateTz)

    // Generate that range as dates
    const dates = Array.from(range.by('day')).map(day => day.format('YYYY-MM-DD'))

    // For each date, assemble incidents for that day and decide day status
    return dates.reduce((acc, cur) => {
      const incidentsForDay = incidentsByDay[cur] || []
      const status = decideDayStatusForService(incidentsForDay)
      acc[cur] = { status, incidents: incidentsForDay }
      return acc
    }, {})
  }

  // console.log(`Generating days info for ${service.attributes.name}`)

  const activeIncidents = activeIncidentsForService()

  // console.log("activeIncidents.length=", activeIncidents.length)
  if (activeIncidents.length > 0) {
    return generateDays(groupIncidentsByDay(activeIncidents))
  }  // all clear
  return generateDays()
}

module.exports = genDays
